import { defineStore } from 'pinia';
import axios from 'axios';

/**
 * Exponential Backoff + Full Jitter (rekomendasi AWS).
 * Mencegah "thundering herd": ketika banyak pengguna mengalami
 * error bersamaan (misal sinyal baru masuk), mereka TIDAK akan
 * retry ke server pada detik yang persis sama.
 *
 * Formula: delay = random(0, min(cap, base * 2^retries))
 *
 * @param {number} retries - percobaan ke-N (0-indexed)
 * @param {number} base    - delay dasar dalam ms (default 1000ms)
 * @param {number} cap     - batas maksimal delay dalam ms (default 8000ms)
 * @returns {number} delay acak dalam ms
 */
export const getRetryDelay = (retries, base = 1000, cap = 8000) =>
  Math.random() * Math.min(cap, base * Math.pow(2, retries));

export const isRetryableError = (error) => {
  const status = error?.response?.status;

  if (!status) return true;

  const retryableStatusCodes = [408, 429, 500, 502, 503, 504];
  return retryableStatusCodes.includes(status);
};

export const getErrorMessage = (error) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  const status = error?.response?.status;
  if (status === 404) return 'Data tidak ditemukan.';

  // Pesan ramah untuk kondisi jaringan lambat / tidak stabil
  const isTimeout =
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ERR_NETWORK' ||
    error?.message?.toLowerCase().includes('timeout') ||
    error?.message?.toLowerCase().includes('network error');

  if (isTimeout) {
    return 'Koneksi internet lambat atau tidak stabil. Silakan coba lagi.';
  }

  return error?.message || 'Terjadi kesalahan yang tidak terduga';
};

export const useLowonganStore = defineStore('lowongan', {
  state: () => ({
    recommendedJobs: [],
    latestJobs: [],
    currentJob: null,
    relatedJobs: [],
    loading: false,
    loadingRecommended: false,     // loading awal recommended (tampilkan shimmer)
    relatedLoading: false,
    error: null,
    recommendedError: null,
    latestError: null,
    jobDetailError: null,
    hasMore: true,
    currentPage: 1,
    totalJobs: 0,
    totalOverallJobs: 0,
    isRetrying: false,
    retryCount: 0,
    isRetryingRecommended: false,  // retry recommended (tampilkan ErrorState)
    retryCountRecommended: 0,
    isRetryingLatest: false,       // retry latest (tampilkan ErrorState)
    retryCountLatest: 0,
    maxRetries: 3,
    filters: {
      search: '',
      location: '',
      education: '',
      jobType: '',
      experience: '',
      gender: '',
      ageRange: '',
    },
  }),

  getters: {
    totalLatestJobs: (state) => state.latestJobs.length,
    hasJob: (state) => state.currentJob !== null,
    canRetry: (state) => state.retryCount < state.maxRetries,
  },

  actions: {
    async fetchRecommendedJobs(options = {}) {
      const { retries = 0 } = options;

      if (retries === 0) {
        // Load awal: tampilkan shimmer
        this.loadingRecommended = true;
        this.isRetryingRecommended = false;
        this.retryCountRecommended = 0;
        this.recommendedError = null;
      }

      try {
        const response = await axios.get('frontend/lowongan/recommended?limit=8');
        this.recommendedJobs = response.data;
        this.recommendedError = null;
        this.isRetryingRecommended = false;
        this.retryCountRecommended = 0;
      } catch (error) {
        if (isRetryableError(error) && retries < this.maxRetries) {
          // Ganti shimmer → ErrorState dengan indikator "Memuat ulang... Percobaan X/3"
          this.loadingRecommended = false;
          this.isRetryingRecommended = true;
          this.retryCountRecommended = retries + 1;
          const delay = getRetryDelay(retries, 1000, 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return this.fetchRecommendedJobs({ retries: retries + 1 });
        }

        // Semua retry habis: tampilkan pesan error + tombol Coba Lagi
        this.recommendedError = getErrorMessage(error);
        this.isRetryingRecommended = false;
        this.retryCountRecommended = 0;
        throw error;
      } finally {
        this.loadingRecommended = false;
      }
    },

    async fetchLatestJobs(page = 1, newFilters = null, options = {}) {
      const { retries = 0 } = options;

      if (this.loading && retries === 0) return;
      this.loading = true;

      if (retries === 0) {
        // Load awal: reset state retry
        this.isRetryingLatest = false;
        this.retryCountLatest = 0;
        this.latestError = null;
        if (page === 1) this.hasMore = true;
      }

      const filters = newFilters || this.filters;
      const params = {
        per_page: 10,
        page,
        search: filters.search || undefined,
        location: filters.location || undefined,
        education: filters.education || undefined,
        job_type: filters.jobType || undefined,
        experience: filters.experience || undefined,
        gender: filters.gender || undefined,
        age_range: filters.ageRange || undefined,
      };

      try {
        const response = await axios.get('frontend/lowongan/latest', { params });

        if (page === 1) {
          this.latestJobs = response.data.data;
        } else {
          this.latestJobs.push(...response.data.data);
        }

        this.hasMore = !!response.data.next_page_url;
        this.currentPage = page;
        this.totalJobs = response.data.total || 0;
        this.totalOverallJobs = response.data.total_overall || 0;
        this.latestError = null;
        this.isRetryingLatest = false;
        this.retryCountLatest = 0;
      } catch (error) {
        if (isRetryableError(error) && retries < this.maxRetries) {
          // Ganti shimmer → ErrorState dengan indikator "Memuat ulang... Percobaan X/3"
          this.isRetryingLatest = true;
          this.retryCountLatest = retries + 1;
          const delay = getRetryDelay(retries, 1000, 8000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          this.loading = false;
          return this.fetchLatestJobs(page, newFilters, { retries: retries + 1 });
        }

        // Semua retry habis: tampilkan pesan error + tombol Coba Lagi
        this.latestError = getErrorMessage(error);
        this.isRetryingLatest = false;
        this.retryCountLatest = 0;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loading) return;
      await this.fetchLatestJobs(this.currentPage + 1);
    },

    async retryRecommended() {
      this.recommendedError = null;
      this.isRetryingRecommended = false;
      this.retryCountRecommended = 0;
      await this.fetchRecommendedJobs();
    },

    async retryLatest() {
      this.latestError = null;
      this.isRetryingLatest = false;
      this.retryCountLatest = 0;
      await this.fetchLatestJobs(1);
    },

    async refresh() {
      this.latestJobs = [];
      this.recommendedJobs = [];
      this.currentPage = 1;
      this.hasMore = true;
      this.totalJobs = 0;
      this.totalOverallJobs = 0;
      this.recommendedError = null;
      this.latestError = null;
      await Promise.all([this.fetchRecommendedJobs(), this.fetchLatestJobs(1)]);
    },

    async setFilters(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.currentPage = 1;
      this.hasMore = true;
      this.latestJobs = [];
      this.totalJobs = 0;
      this.latestError = null;
      await this.fetchLatestJobs(1, this.filters);
    },

    async resetFilters() {
      this.filters = {
        search: '',
        location: '',
        education: '',
        jobType: '',
        experience: '',
        gender: '',
        ageRange: '',
      };
      this.currentPage = 1;
      this.hasMore = true;
      this.latestJobs = [];
      this.totalJobs = 0;
      this.latestError = null;
      await this.fetchLatestJobs(1);
    },

    async fetchJobDetail(slug, options = {}) {
      const { retries = 0 } = options;
      this.loading = true;
      this.error = null;
      this.jobDetailError = null;
      this.currentJob = null;
      this.isRetrying = retries > 0;
      this.retryCount = retries;

      try {
        const response = await axios.get(`frontend/lowongan/${slug}`);
        this.currentJob = response.data;
        this.isRetrying = false;
        this.retryCount = 0;
        return response.data;
      } catch (error) {
        this.error = getErrorMessage(error);

        if (isRetryableError(error) && retries < this.maxRetries) {
          this.isRetrying = true;
          this.retryCount = retries + 1;
          const delay = getRetryDelay(retries, 1000, 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return this.fetchJobDetail(slug, { retries: retries + 1 });
        }

        if (!isRetryableError(error)) {
          this.jobDetailError = this.error;
        }
        this.isRetrying = false;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchRelatedJobs(slug, limit = 5) {
      this.relatedLoading = true;
      try {
        const response = await axios.get(`frontend/lowongan/${slug}/related?limit=${limit}`);
        this.relatedJobs = response.data;
        return response.data;
      } catch {
        this.relatedJobs = [];
        return [];
      } finally {
        this.relatedLoading = false;
      }
    },

    async createJob(formData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post('frontend/lowongan/public', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'Terjadi kesalahan saat mengirim lowongan';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    clearErrors() {
      this.error = null;
      this.recommendedError = null;
      this.latestError = null;
      this.jobDetailError = null;
      this.isRetrying = false;
      this.retryCount = 0;
      this.loadingRecommended = false;
      this.isRetryingRecommended = false;
      this.retryCountRecommended = 0;
      this.isRetryingLatest = false;
      this.retryCountLatest = 0;
    },
  },
});
